import type {
  AccountInfo,
  IPublicClientApplication,
} from "@azure/msal-browser";
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { captureException } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { config } from "~initializer/config.ts";
import { measureSince } from "~lib/measureSince.ts";
import { apiScopes } from "./config";
import { isTransientTokenError } from "./isTransientTokenError.ts";

/**
 * This is a workaround for the following bug in msal-browser
 * @link https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206#issuecomment-961505563
 */
function shouldForceRefresh(idToken: AccountInfo["idTokenClaims"]) {
  return Boolean(idToken?.exp && new Date(idToken.exp * 1000) < new Date());
}

let refreshRequested = false;
let lastForcedRefreshAt = 0;
// cooldown so a persistent 401 cannot hammer AAD with refresh requests
const FORCED_REFRESH_COOLDOWN_MS = 30_000;

let lastFailureReportAt = 0;
// cooldown so the burst of parallel requests hitting one failure reports it once
const FAILURE_REPORT_COOLDOWN_MS = 60_000;

function reportTokenFailure(error: unknown) {
  if (Date.now() - lastFailureReportAt < FAILURE_REPORT_COOLDOWN_MS) {
    return;
  }
  lastFailureReportAt = Date.now();
  captureException(error);
}

// Called on api 401s: msal may serve a cached token the server already rejects.
export function requestTokenRefresh() {
  refreshRequested = true;
}

function consumeRefreshRequest() {
  if (!refreshRequested) {
    return false;
  }
  refreshRequested = false;
  if (Date.now() - lastForcedRefreshAt < FORCED_REFRESH_COOLDOWN_MS) {
    return false;
  }
  lastForcedRefreshAt = Date.now();
  return true;
}

const redirectToLogin = async (
  instance: IPublicClientApplication,
  request: { account?: AccountInfo; scopes: string[]; forceRefresh?: boolean },
  inProgress: InteractionStatus,
) => {
  if (inProgress !== InteractionStatus.None) {
    return;
  }
  try {
    await instance.acquireTokenRedirect({
      ...request,
      // no blank.html here: this URL is the browser's redirect target
      redirectUri: config().msalRedirectUri,
    });
  } catch (redirectError) {
    // losing the race to a parallel redirect is expected
    if (
      !(
        redirectError instanceof BrowserAuthError &&
        redirectError.errorCode === BrowserAuthErrorCodes.interactionInProgress
      )
    ) {
      throw redirectError;
    }
  }
};

export const fetchToken = async (
  instance: IPublicClientApplication,
  scopes: string[],
  inProgress: InteractionStatus,
) => {
  const started = performance.now();
  try {
    return await acquireToken(instance, scopes, inProgress);
  } finally {
    measureSince("fetchToken", started);
  }
};

const acquireToken = async (
  instance: IPublicClientApplication,
  scopes: string[],
  inProgress: InteractionStatus,
) => {
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (!account) {
    // cache emptied while msal-react still reports authenticated (e.g. logout in another tab)
    await redirectToLogin(instance, { scopes }, inProgress);
    return null;
  }

  const forceRefresh =
    shouldForceRefresh(account.idTokenClaims) || consumeRefreshRequest();
  const request = { account, scopes, forceRefresh };

  try {
    return (
      await instance.acquireTokenSilent({
        ...request,
        // blank.html only runs msalRedirectBridge.ts, which posts the response msal waits for
        redirectUri: `${config().msalRedirectUri}/blank.html`,
      })
    ).accessToken;
  } catch (e) {
    // timed_out while online means the renewal iframe is blocked (e.g. third-party cookie protection); only the redirect below recovers
    const timedOutWhileOnline =
      e instanceof BrowserAuthError &&
      e.errorCode === BrowserAuthErrorCodes.timedOut &&
      navigator.onLine;
    // offline: rethrow so React Query retries on reconnect instead of redirecting to login
    if (isTransientTokenError(e) && !timedOutWhileOnline) {
      throw e;
    }
    // a session that AAD wants re-authenticated (login_required, expired MFA) is the user's state, not a bug
    if (!(e instanceof InteractionRequiredAuthError)) {
      reportTokenFailure(e);
    }
    await redirectToLogin(instance, request, inProgress);
  }
  return null;
};

export function useAccessToken() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user = instance.getActiveAccount();

  return useQuery({
    queryKey: ["accessToken", user?.username || ""],
    queryFn: async () => fetchToken(instance, apiScopes(), inProgress),
    enabled: isAuthenticated && inProgress === "none",
    staleTime: 0,
    retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 2000),
  });
}
